import React, { useState } from "react";
import { Plus, X, Loader2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, authHeaders } from "./shared";

export default function OrderForm({ clients, inventory, editOrder, onClose, onSave }) {
  const isEdit = !!editOrder;
  const [form, setForm] = useState(() => {
    if (editOrder) {
      const d = editOrder.destination || {};
      return {
        client_email: editOrder.client_email || clients[0]?.email || "",
        items: editOrder.items?.length ? editOrder.items : [{ name: "", quantity: 1, unit: "pz" }],
        destination_name: d.name || "", destination_company: d.company || "", destination_address: d.address || "",
        destination_city: d.city || "", destination_cap: d.cap || "", destination_province: d.province || "", destination_phone: d.phone || "",
        reference: editOrder.reference || "", cost: editOrder.cost || 0, note: editOrder.note || "",
      };
    }
    return {
      client_email: clients[0]?.email || "",
      items: [{ name: "", quantity: 1, unit: "pz" }],
      destination_name: "", destination_company: "", destination_address: "",
      destination_city: "", destination_cap: "", destination_province: "", destination_phone: "",
      reference: "", cost: 0, note: "",
    };
  });
  const [saving, setSaving] = useState(false);

  const clientInv = inventory.filter(i => i.client_email === form.client_email);
  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", quantity: 1, unit: "pz" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, value) => { const items = [...form.items]; items[i] = { ...items[i], [field]: value }; setForm({ ...form, items }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_email) return toast.error("Seleziona un cliente");
    if (!form.items.some(i => i.name && i.quantity > 0)) return toast.error("Aggiungi almeno un articolo");
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/warehouse/orders/${editOrder.id}`, { ...form, cost: parseFloat(form.cost) || 0 }, authHeaders());
        toast.success("Ordine aggiornato");
      } else {
        await axios.post(`${API}/admin/warehouse/orders`, { ...form, cost: parseFloat(form.cost) || 0 }, authHeaders());
        toast.success("Ordine preparato con successo");
      }
      onClose(); onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-4 overflow-y-auto">
      <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl m-4" data-testid="order-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-heading font-bold text-lg uppercase tracking-wide">{isEdit ? "Modifica Ordine" : "Preparazione Ordine"}</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Cliente *</label>
              <select value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} data-testid="order-client"
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm">
                {clients.map(c => <option key={c.email} value={c.email}>{c.company || c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Riferimento</label>
              <input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="ORD-001"
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase text-zinc-500">Articoli da prelevare *</label>
              <button type="button" onClick={addItem} className="text-xs text-[#FF5200] font-bold flex items-center gap-1"><Plus size={14} /> Aggiungi</button>
            </div>
            {form.items.map((item, i) => (
              <div key={i} className="flex items-center gap-2 bg-zinc-50 p-2 rounded-sm mb-2">
                <select value={item.name} onChange={e => updateItem(i, "name", e.target.value)} data-testid={`order-item-${i}`}
                  className="flex-1 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm">
                  <option value="">-- Seleziona --</option>
                  {clientInv.map(inv => <option key={inv.item_name} value={inv.item_name}>{inv.item_name} (disp: {inv.quantity})</option>)}
                </select>
                <input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, "quantity", parseInt(e.target.value) || 0)}
                  className="w-20 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm text-center" />
                <span className="text-xs text-zinc-400 w-8">{item.unit}</span>
                {form.items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="p-1 text-zinc-300 hover:text-red-500"><X size={16} /></button>}
              </div>
            ))}
          </div>

          <fieldset className="border border-zinc-200 rounded-sm p-4">
            <legend className="text-xs font-bold uppercase text-zinc-500 px-2">Destinatario</legend>
            <div className="grid grid-cols-2 gap-3">
              <input value={form.destination_name} onChange={e => setForm({ ...form, destination_name: e.target.value })} placeholder="Nome" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_company} onChange={e => setForm({ ...form, destination_company: e.target.value })} placeholder="Azienda" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_address} onChange={e => setForm({ ...form, destination_address: e.target.value })} placeholder="Indirizzo" className="col-span-2 border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_city} onChange={e => setForm({ ...form, destination_city: e.target.value })} placeholder="Citta" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_cap} onChange={e => setForm({ ...form, destination_cap: e.target.value })} placeholder="CAP" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_province} onChange={e => setForm({ ...form, destination_province: e.target.value })} placeholder="Prov." className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_phone} onChange={e => setForm({ ...form, destination_phone: e.target.value })} placeholder="Telefono" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
            </div>
          </fieldset>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Costo Preparazione (EUR)</label>
              <input type="number" step="0.01" min="0" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })}
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" data-testid="order-cost" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Note</label>
              <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Note..."
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
            </div>
          </div>

          <button type="submit" disabled={saving} data-testid="order-save-btn"
            className="w-full bg-[#FF5200] text-white h-11 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <ClipboardList size={16} />} {isEdit ? "Salva Modifiche" : "Prepara Ordine e Genera DDT"}
          </button>
        </form>
      </div>
    </div>
  );
}
